import { useState, useCallback } from 'react';
import type { LegalBids } from '../../types/problems';
import { SuitSymbol } from '../common/SuitSymbol';

/**
 * The call pad from `table-practice.html` (feature 023, FR-029): a row of rank
 * chips, a row of strain chips, then a full-width PASS chip.
 *
 * This is the restyled successor to `BidInput`. It deliberately reuses the SAME
 * `LegalBids` contract that `useLegalBids` already produces, so legality logic
 * is not reimplemented here — only the presentation changes. `BidInput` stays in
 * place until every caller has moved over.
 *
 * Interaction: pick a rank, then a strain — the pair is submitted as one call.
 * Pass/Double/Redouble submit immediately. Illegal calls are HIDDEN rather than
 * disabled, and every submission is re-checked on click (defense in depth) — both
 * carried over from `BidInput`, so the two pads behave identically and only look
 * different. A level whose every strain is already outbid disappears with them.
 *
 * The special calls read "Dbl" / "Rdbl", not the raw "X" / "XX" tokens, matching
 * `BidInput` and the auction display. The tokens are still what gets submitted.
 */

export interface CallPadProps {
  onSubmit: (call: string) => void;
  disabled?: boolean;
  /** Legal calls for the current auction state; unrestricted when omitted. */
  legalBids?: LegalBids;
  /** Notifies the parent so it can recompute suits for the chosen level. */
  onLevelChange?: (level: number | null) => void;
}

const ALL_LEVELS = [1, 2, 3, 4, 5, 6, 7];

/**
 * The four suits render through the shared `SuitSymbol` (constitution's Suit Symbol
 * Display standard), so their colour and size are the hand display's, not this pad's.
 * The pad used to draw its own glyphs in the reference design's four-colour scheme,
 * which left them black here — `text-ink` in CHIP_BASE beat the per-chip tone,
 * whichever way Tailwind happened to order the two classes.
 *
 * NT is not a suit and has no symbol, so it stays plain text.
 */
const STRAINS = [
  { value: 'C', name: 'Clubs' },
  { value: 'D', name: 'Diamonds' },
  { value: 'H', name: 'Hearts' },
  { value: 'S', name: 'Spades' },
  { value: 'NT', name: 'No Trump' },
] as const;

// padswap (short single-column viewports — tailwind.config.js): slimmer chips and row gaps.
// Measured on the "Which bid instead?" step of the opening-bid flow at 384×698, the reference
// py-2.5 pad overran the felt frame by 18px — exactly the scrollbar the app-like contract bans.
// py-1.5 keeps the chips ~30px tall, in line with this surface's standing accept-smaller-targets
// decision for phones (see project memory: touch targets deferred until small phones retire).
const CHIP_BASE =
  'flex-1 rounded-lg border border-line-strong bg-card py-2.5 text-center text-base font-bold text-ink ' +
  'shadow-[0_2px_0_#d8ceae] transition-colors ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-1 ' +
  'disabled:cursor-not-allowed disabled:opacity-40 padswap:py-1.5';

const CHIP_SELECTED = 'bg-primary-600 text-white border-primary-700 shadow-[0_2px_0_#063d21]';

export function CallPad({ onSubmit, disabled = false, legalBids, onLevelChange }: CallPadProps) {
  const [level, setLevel] = useState<number | null>(null);

  const availableLevels = legalBids?.levels ?? ALL_LEVELS;
  const availableStrains = legalBids?.suits ?? STRAINS.map((s) => s.value);
  const canPass = legalBids?.canPass ?? true;
  const canDouble = legalBids?.canDouble ?? true;
  const canRedouble = legalBids?.canRedouble ?? true;

  const handleLevel = useCallback(
    (l: number) => {
      const next = level === l ? null : l;
      setLevel(next);
      onLevelChange?.(next);
    },
    [level, onLevelChange]
  );

  const handleStrain = useCallback(
    (strain: string) => {
      if (level === null) return;
      // Defense in depth: never submit a call the auction state forbids, even if
      // a stale render left the chip enabled.
      if (legalBids && (!legalBids.levels.includes(level) || !legalBids.suits.includes(strain))) {
        return;
      }
      onSubmit(`${level}${strain}`);
      setLevel(null);
      onLevelChange?.(null);
    },
    [level, legalBids, onSubmit, onLevelChange]
  );

  const handleSpecial = useCallback(
    (call: string) => {
      // Defense in depth, as on the strain chips: a stale render must never be able to
      // submit a Double the auction does not allow.
      if (legalBids) {
        if (call === 'X' && !legalBids.canDouble) return;
        if (call === 'XX' && !legalBids.canRedouble) return;
        if (call === 'Pass' && !legalBids.canPass) return;
      }
      onSubmit(call);
      setLevel(null);
      onLevelChange?.(null);
    },
    [legalBids, onSubmit, onLevelChange]
  );

  const shownLevels = ALL_LEVELS.filter((l) => availableLevels.includes(l));
  const shownStrains = STRAINS.filter((s) => availableStrains.includes(s.value));

  // `data-count` is how many chips the row actually holds. index.css wraps a row into a grid when
  // the pad is too narrow for that many chips at 38px each (client, 2026-10-02: no chip narrower
  // than 38px); a row of seven needs more width before it wraps than a row of four.
  return (
    <div className="space-y-2.5 padswap:space-y-2" data-testid="call-pad">
      <div className="flex gap-2" role="group" aria-label="Bid level" data-count={shownLevels.length}>
        {shownLevels.map((l) => {
          const selected = level === l;
          return (
            <button
              key={l}
              type="button"
              disabled={disabled}
              aria-pressed={selected}
              onClick={() => handleLevel(l)}
              className={`${CHIP_BASE} ${selected ? CHIP_SELECTED : ''}`}
            >
              {l}
            </button>
          );
        })}
      </div>

      <div className="flex gap-2" role="group" aria-label="Bid strain" data-count={shownStrains.length}>
        {shownStrains.map((s) => (
          <button
            key={s.value}
            type="button"
            disabled={disabled || level === null}
            onClick={() => handleStrain(s.value)}
            aria-label={level === null ? s.name : `${level} ${s.name}`}
            className={`${CHIP_BASE} leading-none`}
          >
            {/* The button's own aria-label names the call, and an aria-label on the
                button overrides its contents, so the glyph is never announced twice.
                ml-0 drops the bid-token gap: that space separates a glyph from the
                digit before it, and here the glyph stands alone in a centred chip. */}
            {s.value === 'NT' ? 'NT' : <SuitSymbol suit={s.value} className="ml-0" />}
          </button>
        ))}
      </div>

      {/* The calls that need no level. Stacked — Dbl/Rdbl, then a full-width Pass — wherever there
          is height for it. On short screens (padswap, short, narrow — the bands where the "No" path
          also swaps its steps rather than stacking them) they share ONE row: in a competitive
          auction the stacked Dbl row was the height that ran the pad 4px past the frame on the
          iPhone SE and made the answer panel scroll at 754x303 (spec 031, 2026-10-01). */}
      {(canDouble || canRedouble || canPass) && (
        // `call-pad-specials` is how index.css's short-phone bands still reach Pass to compact it.
        <div className="call-pad-specials flex flex-col gap-2.5 padswap:flex-row padswap:gap-2 short:flex-row short:gap-2 narrow:flex-row narrow:gap-2">
          {(canDouble || canRedouble) && (
            <div className="flex gap-2 padswap:flex-1 short:flex-1 narrow:flex-1">
              {canDouble && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => handleSpecial('X')}
                  className={CHIP_BASE}
                  aria-label="Double"
                >
                  Dbl
                </button>
              )}
              {canRedouble && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => handleSpecial('XX')}
                  className={CHIP_BASE}
                  aria-label="Redouble"
                >
                  Rdbl
                </button>
              )}
            </div>
          )}

          {canPass && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => handleSpecial('Pass')}
              className={
                'w-full rounded-lg bg-primary-600 py-2.5 text-center text-[15px] font-bold tracking-wider text-white ' +
                'shadow-[0_2px_0_#063d21] transition-colors hover:bg-primary-700 ' +
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brass focus-visible:ring-offset-1 ' +
                'disabled:cursor-not-allowed disabled:opacity-40 ' +
                'padswap:min-w-0 padswap:flex-1 short:min-w-0 short:flex-1 narrow:min-w-0 narrow:flex-1'
              }
            >
              Pass
            </button>
          )}
        </div>
      )}
    </div>
  );
}
